/**
 * Provider-agnostic ParticipantTile Component
 *
 * Renders participant video tiles for both Dyte and LiveKit providers.
 */

import { useEffect, useRef, memo } from 'react';
import { RtkParticipantTile } from '@cloudflare/realtimekit-react-ui';
import { useMeetingProvider } from 'contexts/MeetingProviderContext';
import type { IMeetingParticipant } from 'adapters/MeetingAdapter';
import StudentAwayBadge from './StudentAwayBadge';

interface ParticipantTileProps {
  participant: IMeetingParticipant;
  meeting: unknown;
  size?: 'sm' | 'md' | 'lg';
  states?: unknown;
  style?: React.CSSProperties;
  // Student switched tab or left full screen (socket-server relays it to the recorder).
  isAway?: boolean;
  [key: string]: unknown;
}

/**
 * LiveKit Participant Tile
 * Renders video using native HTML5 video element
 * Memoized to prevent unnecessary rerenders
 */
const LiveKitParticipantTile: React.FC<ParticipantTileProps> = memo(({
  participant,
  style,
  size = 'md',
  isAway = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Track the current track IDs to detect actual track changes
  const videoTrackIdRef = useRef<string | null>(null);
  const audioTrackIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!participant) return;

    const videoTrack = participant.videoTrack;
    const currentVideoTrackId = videoTrack?.id || null;

    // Only update video if the track ID actually changed
    if (currentVideoTrackId !== videoTrackIdRef.current) {
      videoTrackIdRef.current = currentVideoTrackId;

      if (videoTrack && videoRef.current) {
        const videoStream = new MediaStream([videoTrack]);
        videoRef.current.srcObject = videoStream;
        videoRef.current.play().catch((err) => {
          console.error('Error playing video:', err);
        });
      } else if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  }, [participant?.videoTrack?.id, participant]);

  useEffect(() => {
    if (!participant) return;

    const audioTrack = participant.audioTrack;
    const currentAudioTrackId = audioTrack?.id || null;

    // Only update audio if the track ID actually changed
    if (currentAudioTrackId !== audioTrackIdRef.current) {
      audioTrackIdRef.current = currentAudioTrackId;

      if (audioTrack && audioRef.current) {
        const audioStream = new MediaStream([audioTrack]);
        audioRef.current.srcObject = audioStream;
        audioRef.current.play().catch((err) => {
          console.error('Error playing audio:', err);
        });
      } else if (audioRef.current) {
        audioRef.current.srcObject = null;
      }
    }
  }, [participant?.audioTrack?.id, participant]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      if (audioRef.current) {
        audioRef.current.srcObject = null;
      }
    };
  }, []);

  if (!participant) {
    return null;
  }

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <div
      style={{
        ...style,
        position: 'relative',
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: participant.videoEnabled ? 'block' : 'none',
        }}
      />

      {/* Audio element (hidden) */}
      <audio ref={audioRef} autoPlay />

      {/* Fallback when video is disabled */}
      {!participant.videoEnabled && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#2a2a2a',
          }}
        >
          <div
            style={{
              // Scales with the tile so it stays a circle in the narrow
              // parent strip instead of overflowing a small tile.
              width: 'min(40%, 64px)',
              aspectRatio: '1',
              borderRadius: '50%',
              backgroundColor: '#4a4a4a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'clamp(14px, 2.2vw, 24px)',
              color: '#ffffff',
              fontWeight: 'bold',
            }}
          >
            {participant.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        </div>
      )}

      {/* Name overlay */}
      <div
        style={{
          position: 'absolute',
          bottom: '8px',
          left: '8px',
          right: '8px',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        className={sizeClasses[size]}
      >
        <span
          style={{
            // Without this the name wraps and gets clipped by narrow tiles
            // ("Developer Account" -> "Dev / Acc").
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
          title={participant.name}
        >
          {participant.name}
        </span>
        {!participant.audioEnabled && (
          <span style={{ marginLeft: '8px', flexShrink: 0 }}>🔇</span>
        )}
      </div>

      {isAway && <StudentAwayBadge />}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary rerenders
  // Only rerender if these specific properties change
  const prevParticipant = prevProps.participant;
  const nextParticipant = nextProps.participant;

  if (!prevParticipant && !nextParticipant) return true;
  if (!prevParticipant || !nextParticipant) return false;

  // Check if critical properties changed
  const videoTrackChanged = prevParticipant.videoTrack?.id !== nextParticipant.videoTrack?.id;
  const audioTrackChanged = prevParticipant.audioTrack?.id !== nextParticipant.audioTrack?.id;
  const videoEnabledChanged = prevParticipant.videoEnabled !== nextParticipant.videoEnabled;
  const audioEnabledChanged = prevParticipant.audioEnabled !== nextParticipant.audioEnabled;
  const nameChanged = prevParticipant.name !== nextParticipant.name;
  const idChanged = prevParticipant.id !== nextParticipant.id;
  const sizeChanged = prevProps.size !== nextProps.size;
  const awayChanged = prevProps.isAway !== nextProps.isAway;

  // Return true if nothing changed (skip rerender), false if something changed (do rerender)
  return !(videoTrackChanged || audioTrackChanged || videoEnabledChanged ||
           audioEnabledChanged || nameChanged || idChanged || sizeChanged || awayChanged);
});

/**
 * Main ParticipantTile component that routes to the correct provider
 */
export const ParticipantTile: React.FC<ParticipantTileProps> = (props) => {
  const { provider } = useMeetingProvider();

  if (provider === 'livekit') {
    return <LiveKitParticipantTile {...props} />;
  }

  // Default to Dyte
  // Use the raw Dyte participant object for Dyte UI components
  const { isAway, ...rest } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dyteProps: any = {
    ...rest,
    participant: props.participant._raw || props.participant,
    meeting: props.meeting,
  };

  // Always wrapped (not only while away) so the chip toggling never remounts the tile.
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <RtkParticipantTile {...dyteProps} />
      {isAway && <StudentAwayBadge />}
    </div>
  );
};

export default ParticipantTile;
